import { Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabaseClient';

/**
 * Session validation utility for centralized session management
 * Provides reusable functions for checking session validity and handling auth errors
 */

// Cache for session validation to avoid excessive checks
let validationCache: {
    valid: boolean;
    session: Session | null;
    timestamp: number;
} | null = null;

const CACHE_DURATION_MS = 30000; // 30 seconds

// Longer timeout for post-sleep scenarios
const POST_SLEEP_TIMEOUT_MS = 15000; // 15 seconds

// Storage key for auth transition flag (persists across page reloads for OAuth)
const AUTH_TRANSITION_KEY = 'auth_in_transition';
const AUTH_TRANSITION_TIMESTAMP_KEY = 'auth_transition_timestamp';
const AUTH_TRANSITION_MAX_AGE_MS = 60000; // 60 seconds max for transition

/**
 * Check if auth transition flag is still valid (not expired)
 */
function isAuthTransitionFlagValid(): boolean {
    try {
        const timestamp = localStorage.getItem(AUTH_TRANSITION_TIMESTAMP_KEY);
        if (!timestamp) return false;

        const age = Date.now() - parseInt(timestamp, 10);
        return age < AUTH_TRANSITION_MAX_AGE_MS;
    } catch {
        return false;
    }
}

/**
 * Mark auth as in transition (call before OAuth redirect or during callback processing)
 * This prevents validation from failing during the brief period when session is being established
 * Uses localStorage to persist across page reloads (OAuth redirects)
 */
export function setAuthInTransition(inTransition: boolean): void {
    try {
        if (inTransition) {
            console.log('🔄 Auth marked as in transition');
            localStorage.setItem(AUTH_TRANSITION_KEY, 'true');
            localStorage.setItem(AUTH_TRANSITION_TIMESTAMP_KEY, Date.now().toString());
            // Clear any stale cache when entering transition
            validationCache = null;
        } else {
            console.log('✅ Auth transition complete');
            localStorage.removeItem(AUTH_TRANSITION_KEY);
            localStorage.removeItem(AUTH_TRANSITION_TIMESTAMP_KEY);
        }
    } catch (err) {
        console.warn('Failed to update auth transition flag:', err);
    }
}

/**
 * Check if auth is currently in transition
 */
export function isAuthInTransition(): boolean {
    try {
        const inTransition = localStorage.getItem(AUTH_TRANSITION_KEY) === 'true';

        // If flag is set but expired, clear it
        if (inTransition && !isAuthTransitionFlagValid()) {
            console.log('⚠️ Auth transition flag expired, clearing');
            setAuthInTransition(false);
            return false;
        }

        return inTransition;
    } catch {
        return false;
    }
}

/**
 * Wait for auth transition to complete with timeout
 * @param timeoutMs - Maximum time to wait (default 10 seconds)
 */
export async function waitForAuthTransition(timeoutMs = 10000): Promise<void> {
    if (!isAuthInTransition()) return;

    console.log('⏳ Waiting for auth transition to complete...');

    const startTime = Date.now();
    while (isAuthInTransition() && (Date.now() - startTime) < timeoutMs) {
        await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (isAuthInTransition()) {
        console.warn('⚠️ Auth transition wait timed out, clearing flag');
        // Clear the flag if we timed out to prevent permanent blocking
        setAuthInTransition(false);
    }
}

/**
 * Check if current session is valid
 * Uses caching to avoid excessive validation calls
 * @param forceRefresh - Skip cache and force fresh validation
 * @returns Object with validity status and session
 */
export async function validateSession(forceRefresh = false): Promise<{
    valid: boolean;
    session: Session | null;
    reason?: string;
}> {
    try {
        // If auth is in transition (e.g., during SSO callback), wait for it to complete
        if (isAuthInTransition()) {
            console.log('⏳ Auth in transition, waiting before validation...');
            await waitForAuthTransition(8000); // Wait up to 8 seconds

            // If still in transition after waiting, try to validate anyway
            if (isAuthInTransition()) {
                console.warn('⚠️ Auth still in transition, proceeding with validation');
            }
        }

        // Check cache first unless force refresh
        if (!forceRefresh && validationCache) {
            const cacheAge = Date.now() - validationCache.timestamp;
            if (cacheAge < CACHE_DURATION_MS) {
                console.log(`✅ Using cached session validation (${Math.floor(cacheAge / 1000)}s old)`);
                return {
                    valid: validationCache.valid,
                    session: validationCache.session,
                };
            }
        }

        // Create a timeout promise - use longer timeout after PC sleep
        // After sleep, network may take longer to reconnect
        const timeoutMs = forceRefresh ? POST_SLEEP_TIMEOUT_MS : 8000;
        const timeoutPromise = new Promise<{ data: { session: Session | null }, error: any }>((_, reject) => {
            setTimeout(() => reject(new Error('Session validation timed out')), timeoutMs);
        });

        // Get current session with timeout
        const { data: { session }, error } = await Promise.race([
            supabase.auth.getSession(),
            timeoutPromise
        ]) as { data: { session: Session | null }, error: any };

        if (error) {
            console.error('❌ Error getting session:', error);
            validationCache = { valid: false, session: null, timestamp: Date.now() };
            return { valid: false, session: null, reason: 'Error fetching session' };
        }

        if (!session) {
            console.log('❌ No session found');
            validationCache = { valid: false, session: null, timestamp: Date.now() };
            return { valid: false, session: null, reason: 'No session' };
        }

        // Check if token is expired
        const expiresAt = session.expires_at;
        if (!expiresAt) {
            console.warn('⚠️ Session has no expiration time');
            validationCache = { valid: false, session: null, timestamp: Date.now() };
            return { valid: false, session: null, reason: 'Invalid session expiration' };
        }

        const now = Math.floor(Date.now() / 1000);
        const expiresIn = expiresAt - now;

        if (expiresIn <= 0) {
            console.log(`❌ Token expired ${Math.abs(expiresIn)} seconds ago`);
            validationCache = { valid: false, session: null, timestamp: Date.now() };
            return { valid: false, session: null, reason: 'Token expired' };
        }

        // Token is valid
        console.log(`✅ Session valid (expires in ${expiresIn}s)`);
        validationCache = { valid: true, session, timestamp: Date.now() };
        return { valid: true, session };

    } catch (err: any) {
        console.error('❌ Exception in validateSession:', err);
        validationCache = { valid: false, session: null, timestamp: Date.now() };

        // If this is a timeout error, redirect to login immediately
        // Don't try to recover - the network/session is likely completely stale after long sleep
        const errorMessage = err?.message?.toLowerCase() || '';
        if (errorMessage.includes('timed out') || errorMessage.includes('timeout')) {
            console.log('⏱️ Session validation timed out - redirecting to login');
            // Only redirect if not already on login page
            if (!window.location.pathname.includes('/login')) {
                redirectToLogin('Session validation timed out');
            }
        }

        return { valid: false, session: null, reason: 'Validation exception' };
    }
}

/**
 * Clear the validation cache
 * Useful after login/logout or when you know session state has changed
 */
export function clearValidationCache(): void {
    validationCache = null;
    console.log('🗑️ Session validation cache cleared');
}

/**
 * Clear stale session from localStorage if it's definitely expired
 * This prevents validation loops when session is hours/days old after PC sleep
 * @param forceIfOlderThanMinutes - Clear session if it expired more than X minutes ago (default 30)
 */
export function clearStaleSessionFromStorage(forceIfOlderThanMinutes = 30): boolean {
    try {
        const storageKey = 'supabase.auth.token';
        const storedData = localStorage.getItem(storageKey);

        if (!storedData) {
            console.log('🗑️ No session in localStorage to check');
            return false;
        }

        const sessionData = JSON.parse(storedData);
        const expiresAt = sessionData?.currentSession?.expires_at || sessionData?.expires_at;

        if (!expiresAt) {
            console.log('🗑️ Session has no expiration, clearing as stale');
            localStorage.removeItem(storageKey);
            return true;
        }

        const now = Math.floor(Date.now() / 1000);
        const expiredAgoSeconds = now - expiresAt;
        const expiredAgoMinutes = expiredAgoSeconds / 60;

        if (expiredAgoSeconds > 0 && expiredAgoMinutes > forceIfOlderThanMinutes) {
            console.log(`🗑️ Session expired ${Math.floor(expiredAgoMinutes)} minutes ago, clearing stale session`);
            localStorage.removeItem(storageKey);
            clearValidationCache();
            return true;
        }

        return false;
    } catch (err) {
        console.error('Error checking for stale session:', err);
        return false;
    }
}

/**
 * Check if an error is authentication-related
 * @param error - The error to check
 * @returns true if the error is auth-related
 */
export function isAuthError(error: any): boolean {
    if (!error) return false;

    const errorString = String(error).toLowerCase();
    const errorMessage = error?.message?.toLowerCase() || '';
    const errorCode = error?.code?.toLowerCase() || '';
    const statusCode = error?.status || error?.statusCode || 0;

    // Exclude timeout errors - these are network issues, not auth issues
    // Treating them as auth errors causes infinite retry loops
    if (errorMessage.includes('timed out') || errorMessage.includes('timeout')) {
        return false;
    }

    // Check for common auth error patterns
    const authPatterns = [
        'jwt',
        'token',
        'expired',
        'unauthorized',
        'unauthenticated',
        'invalid session',
        'invalidjwttoken',
        'pgrst301', // PostgREST JWT error
        'pgrst302', // PostgREST missing authorization
        'auth',
        'session',
    ];

    // Check status codes
    if (statusCode === 401 || statusCode === 403) {
        return true;
    }

    // Check error patterns
    for (const pattern of authPatterns) {
        if (
            errorString.includes(pattern) ||
            errorMessage.includes(pattern) ||
            errorCode.includes(pattern)
        ) {
            return true;
        }
    }

    return false;
}

/**
 * Redirect to login page with session expired parameter
 * Uses window.location.href to force a full page reload, ensuring clean state
 * @param reason - Optional reason for redirect (for logging)
 * @param returnUrl - Optional URL to return to after login
 */
export function redirectToLogin(reason?: string, returnUrl?: string): void {
    console.log(`🔄 Redirecting to login${reason ? `: ${reason}` : ''}`);

    // Clear validation cache on redirect
    clearValidationCache();

    // Clear auth transition flag
    setAuthInTransition(false);

    // Build login URL with parameters
    const params = new URLSearchParams();
    params.set('session_expired', 'true');

    if (returnUrl) {
        params.set('return_url', returnUrl);
    }

    if (reason) {
        params.set('reason', reason);
    }

    const loginUrl = `/login?${params.toString()}`;

    // Force a full page reload to ensure clean state
    // This clears any stale React state, event handlers, and hot-reload issues
    window.location.replace(loginUrl);
}

/**
 * Attempt to refresh the current session
 * @param retryCount - Number of retry attempts (default 2)
 * @returns true if refresh was successful
 */
export async function attemptSessionRefresh(retryCount = 2): Promise<boolean> {
    for (let attempt = 1; attempt <= retryCount; attempt++) {
        try {
            console.log(`🔄 Attempting to refresh session (attempt ${attempt}/${retryCount})...`);

            const { data, error } = await supabase.auth.refreshSession();

            if (error) {
                console.error(`❌ Session refresh attempt ${attempt} failed:`, error.message);

                // If it's a network error and we have retries left, wait and retry
                if (attempt < retryCount && (error.message?.includes('network') || error.message?.includes('fetch'))) {
                    console.log(`⏳ Waiting before retry...`);
                    await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
                    continue;
                }

                clearValidationCache();
                return false;
            }

            if (!data.session) {
                console.error(`❌ Session refresh attempt ${attempt} returned no session`);

                if (attempt < retryCount) {
                    await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
                    continue;
                }

                clearValidationCache();
                return false;
            }

            console.log('✅ Session refreshed successfully');
            clearValidationCache(); // Clear cache to force revalidation with new session
            return true;

        } catch (err: any) {
            console.error(`❌ Exception during session refresh attempt ${attempt}:`, err?.message || err);

            if (attempt < retryCount) {
                await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
                continue;
            }

            clearValidationCache();
            return false;
        }
    }

    return false;
}

/**
 * Detect if PC was likely sleeping based on time gap
 * @param lastCheckTime - Timestamp of last check in milliseconds
 * @returns true if time gap suggests PC was sleeping
 */
export function detectPCSleep(lastCheckTime: number): boolean {
    const now = Date.now();
    const timeDiff = now - lastCheckTime;

    // If more than 5 minutes have passed, PC was likely sleeping
    const SLEEP_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

    if (timeDiff > SLEEP_THRESHOLD_MS) {
        console.log(`⚠️ Time gap detected: ${Math.floor(timeDiff / 1000)}s (PC may have been sleeping)`);
        return true;
    }

    return false;
}

/**
 * Check if we're on a public route that doesn't require authentication
 * @param path - The path to check (defaults to current path)
 * @returns true if the path is public
 */
export function isPublicRoute(path?: string): boolean {
    const currentPath = path || window.location.pathname;

    const publicRoutes = [
        '/login',
        '/register',
        '/forgot-password',
        '/reset-password',
        '/auth/callback',
        '/contact',
        '/help',
        '/terms',
        '/privacy',
        '/terms-of-service',
        '/privacy-policy',
        '/unsubscribe',
        '/',
    ];

    return publicRoutes.some(route => currentPath === route || currentPath.startsWith(route));
}

/**
 * Get authenticated user efficiently with session validation
 * Uses getSession() for speed but validates token expiration
 * Falls back to getUser() if session appears expired to trigger proper auth flow
 * 
 * @returns User object or null if not authenticated
 * @throws Error with auth message if session is expired (triggers redirect)
 */
export async function getAuthenticatedUser(): Promise<{ id: string; email?: string } | null> {
    try {
        // First try getSession() - fast, reads from cache
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session) {
            return null;
        }

        // Check if token is expired or about to expire (within 60 seconds)
        const expiresAt = session.expires_at;
        const now = Math.floor(Date.now() / 1000);
        const expiresIn = expiresAt ? expiresAt - now : 0;

        if (expiresIn <= 60) {
            // Token expired or expiring soon - use getUser() to trigger proper validation
            // This will either refresh the token or throw an auth error
            console.log(`⚠️ Token expiring in ${expiresIn}s, validating with server...`);
            const { data: { user }, error: userError } = await supabase.auth.getUser();

            if (userError) {
                console.error('❌ Session validation failed:', userError.message);
                throw userError; // This will be caught by global error handlers
            }

            return user;
        }

        // Token is valid, return user from session
        return session.user;
    } catch (err) {
        console.error('Error in getAuthenticatedUser:', err);
        throw err;
    }
}

/**
 * QUICK SYNCHRONOUS token expiration check
 * Reads session from localStorage and checks if token is expired
 * This is fast enough to use on every click without blocking UI
 * 
 * @returns Object with isExpired and expiresIn (seconds until expiration, negative if expired)
 */
export function checkTokenExpirationSync(): { isExpired: boolean; expiresIn: number; hasSession: boolean } {
    try {
        // Read session data directly from localStorage (same key Supabase uses)
        const storageKey = 'supabase.auth.token';
        const storedData = localStorage.getItem(storageKey);

        if (!storedData) {
            return { isExpired: true, expiresIn: 0, hasSession: false };
        }

        const sessionData = JSON.parse(storedData);

        // Handle both old and new Supabase storage formats
        const expiresAt = sessionData?.currentSession?.expires_at || sessionData?.expires_at;

        if (!expiresAt) {
            // No expiration date found, assume expired
            return { isExpired: true, expiresIn: 0, hasSession: true };
        }

        const now = Math.floor(Date.now() / 1000);
        const expiresIn = expiresAt - now;

        // Consider expired if less than 60 seconds remaining or already expired
        const isExpired = expiresIn < 60;

        return { isExpired, expiresIn, hasSession: true };
    } catch (err) {
        console.error('Error checking token expiration:', err);
        // On error, assume expired to be safe
        return { isExpired: true, expiresIn: 0, hasSession: false };
    }
}

/**
 * Validate session and handle expiration before navigation
 * Use this on navigation clicks to detect expired sessions
 * 
 * @returns true if session is valid, false if expired (will redirect to login)
 */
export async function validateAndNavigate(): Promise<boolean> {
    // Quick sync check first
    const { isExpired, hasSession } = checkTokenExpirationSync();

    if (!hasSession) {
        console.log('❌ No session found, redirecting to login');
        redirectToLogin('No session');
        return false;
    }

    if (isExpired) {
        console.log('⚠️ Token appears expired, attempting refresh...');

        // Try to refresh before giving up
        const refreshSuccess = await attemptSessionRefresh();

        if (!refreshSuccess) {
            console.log('❌ Session refresh failed, redirecting to login');
            redirectToLogin('Session expired');
            return false;
        }

        console.log('✅ Session refreshed successfully');
        clearValidationCache();
    }

    return true;
}
